import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Newspaper } from 'lucide-react';
import SiteLayout from '../common/SiteLayout';
import { ARTICLES, getArticleBySlug } from './articles';

// On article pages the in-page anchors live on the home page, so point the
// shared navbar/footer links back to the landing page sections.
const NAV_LINKS = [
  { href: '/#how-it-works', label: 'How it works' },
  { href: '/#strategies', label: 'Strategies' },
  { href: '/#channels', label: 'Channels' },
  { href: '/#savings', label: 'Savings' },
  { href: '/#integrations', label: 'Integrations' },
  { href: '/#stories', label: 'Stories' },
];

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const nextArticle = ARTICLES.find((a) => a.slug !== slug);

  if (!article) {
    return (
      <SiteLayout navLinks={NAV_LINKS} logoHref="/">
        <section className="bg-white">
          <div className="container-page py-24 lg:py-32 text-center">
            <p className="eyebrow">404</p>
            <h1 className="mt-3 h-display text-3xl sm:text-4xl font-bold">
              Story not found
            </h1>
            <p className="mt-4 text-ink-600">
              The story you’re looking for doesn’t exist or has moved.
            </p>
            <Link to="/#stories" className="btn-primary mt-8">
              <ArrowLeft className="h-4 w-4" />
              Back to all stories
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navLinks={NAV_LINKS} logoHref="/">
      <article className="bg-white">
        <div className="relative overflow-hidden border-b border-ink-200">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-[420px] bg-radial-fade pointer-events-none"
          />
          <div className="container-page relative pt-14 pb-12 lg:pt-16 lg:pb-16">
            <Link
              to="/#stories"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-600 hover:text-ink-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              All stories
            </Link>

            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider font-semibold text-ink-500">
              <Newspaper className="h-4 w-4" />
              {article.kicker}
              <span aria-hidden="true" className="text-ink-300">·</span>
              <span className="text-ink-400 normal-case tracking-normal font-medium">
                {article.date}
              </span>
              <span aria-hidden="true" className="text-ink-300">·</span>
              <span className="text-ink-400 normal-case tracking-normal font-medium">
                {article.readingTime}
              </span>
            </div>

            <h1 className="mt-4 max-w-4xl h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] text-balance">
              {article.title}
            </h1>

            <p className="mt-6 max-w-2xl text-lg text-ink-600 text-balance">
              {article.excerpt}
            </p>
          </div>
        </div>

        <div className="container-page py-14 lg:py-20">
          <div className="max-w-2xl mx-auto">
            {article.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            <div className="mt-12 pt-8 border-t border-ink-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/#stories"
                className="inline-flex items-center gap-2 text-sm font-semibold text-ink-900 hover:text-ink-600"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all stories
              </Link>

              {nextArticle && (
                <Link
                  to={`/stories/${nextArticle.slug}`}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-ink-900 hover:text-ink-600"
                >
                  Read next: {nextArticle.kicker}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}

function Block({ block }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="mt-10 mb-4 h-display text-xl sm:text-2xl font-bold text-ink-900">
          {block.text}
        </h2>
      );
    case 'quote':
      return (
        <blockquote className="my-8 border-l-2 border-ink-900 pl-5 sm:pl-6">
          <p className="text-lg sm:text-xl font-medium text-ink-900 leading-relaxed text-balance">
            “{block.text}”
          </p>
          {block.cite && (
            <footer className="mt-3 text-sm font-semibold text-ink-500">
              — {block.cite}
            </footer>
          )}
        </blockquote>
      );
    case 'divider':
      return (
        <div className="my-10 text-center text-ink-300 tracking-[0.4em] select-none">
          ###
        </div>
      );
    case 'paragraph':
    default:
      return (
        <p className="mt-5 text-ink-700 leading-relaxed text-[1.0625rem]">
          {block.text}
        </p>
      );
  }
}
